/**
 * As chamadas da tela de parametrização (`TP-051`, T10/T11) — tudo o que a
 * tela sabe sobre `/contratos/{id}/parametrizacao/ler-do-iw` e
 * `/parametrizacao/de-para` passa por aqui, mesma disciplina de
 * `features/contratos/api/contratos.ts`.
 */

import { queryOptions } from '@tanstack/react-query';

import { requisitar, requisitarComStatus } from '../../../shared/api/clienteHttp';
import { mapearItemDaFilaDePara, mapearReconciliacao } from './mapeadores';
import type {
  ItemDaFilaDeParaApi,
  ItemDaFilaDePara,
  ItemDoContrato,
  ItemDoContratoApi,
  Reconciliacao,
  ReconciliacaoApi,
} from './tipos';

export const chavesDeParametrizacao = {
  raiz: ['parametrizacao'] as const,
  fila: (contratoId: string) => [...chavesDeParametrizacao.raiz, 'fila', contratoId] as const,
};

export function opcoesDaFilaDePara(contratoId: string) {
  return queryOptions({
    queryKey: chavesDeParametrizacao.fila(contratoId),
    queryFn: ({ signal }): Promise<ItemDaFilaDePara[]> => {
      const parametros = new URLSearchParams({ contratoId });
      return requisitar<ItemDaFilaDeParaApi[]>(`/parametrizacao/de-para?${parametros.toString()}`, {
        sinal: signal,
      }).then((itens) => itens.map(mapearItemDaFilaDePara));
    },
    enabled: contratoId !== '',
  });
}

/**
 * `POST /contratos/{id}/parametrizacao/ler-do-iw` (TP-071 T9): a mesma
 * reconciliação com a tabela de preços e o de-para lidos do IW. `criada`
 * vem do status: `200` é a MESMA avaliação (`D11`), `201` é nova.
 */
export async function lerCatalogoDoIw(parametros: {
  contratoId: string;
  competencia: string;
  /** Lê de novo no IW, sem a cache de 5 minutos da API. */
  atualizar?: boolean;
}): Promise<{ reconciliacao: Reconciliacao; criada: boolean }> {
  const { dado, status } = await requisitarComStatus<ReconciliacaoApi>(
    `/contratos/${encodeURIComponent(parametros.contratoId)}/parametrizacao/ler-do-iw?competencia=${encodeURIComponent(parametros.competencia)}${parametros.atualizar === true ? '&atualizar=true' : ''}`,
    { method: 'POST' },
  );

  return { reconciliacao: mapearReconciliacao(dado), criada: status === 201 };
}

/**
 * `POST /parametrizacao/de-para/confirmar` (T11). Recusar um vínculo NÃO
 * tem endpoint (`DI40` do TP-051) — é só não chamar esta função.
 */
export async function confirmarDePara(itemContratualId: string, codigoIw: string): Promise<void> {
  await requisitar('/parametrizacao/de-para/confirmar', {
    method: 'POST',
    corpo: { itemContratualId, codigoIw },
  });
}

/** Os vínculos confirmados num clique só (mesmo código e mesmo preço dos dois lados); devolve quantos foram gravados. */
export async function confirmarDeParaEmLote(
  vinculos: readonly { itemContratualId: string; codigoIw: string }[],
): Promise<number> {
  const resposta = await requisitar<{ confirmados: number }>('/parametrizacao/de-para/confirmar-lote', {
    method: 'POST',
    corpo: { vinculos },
  });
  return resposta.confirmados;
}

/**
 * `GET /contratos/{id}/itens` com busca (`TP-071`, T11): os itens do contrato
 * para escolher no vínculo manual. A chamada é repetida aqui, e não importada
 * de `features/contratos`, pela parede de boundaries.
 */
export async function buscarItensDoContrato(parametros: {
  contratoId: string;
  competencia: string;
  busca: string;
}): Promise<ItemDoContrato[]> {
  const consulta = new URLSearchParams({ competencia: parametros.competencia, busca: parametros.busca, tamanho: '20' });
  const pagina = await requisitar<{ items: readonly ItemDoContratoApi[] | null }>(
    `/contratos/${encodeURIComponent(parametros.contratoId)}/itens?${consulta.toString()}`,
  );
  return (pagina.items ?? []).map((i) => ({
    id: i.id,
    codigo: i.code ?? '',
    descricao: i.description ?? '',
    valor: i.value,
  }));
}
